import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

const CONFIDENCE_BANDS = [
  {
    label: 'Established',
    description:
      'confirmed by an originating or authoritative source, or by the strongest available corroboration.',
  },
  {
    label: 'Corroborated',
    description:
      'supported by multiple independent credible sources, even if not officially confirmed.',
  },
  {
    label: 'Reported',
    description:
      'publicly reported by at least one credible source, without enough independent corroboration to call it established.',
  },
  {
    label: 'Single Source',
    description: 'based on exactly one source and presented with that caveat.',
  },
  {
    label: 'Unverified',
    description:
      'weak or speculative reporting that should not be treated as established fact.',
  },
] as const

const SOURCE_TIERS = [
  {
    label: 'T1 Authoritative',
    description:
      'official primary sources such as government CERTs, vendor PSIRTs, standards bodies, CISA, MSRC, MITRE, and NVD.',
  },
  {
    label: 'T2 Research',
    description:
      'vendor threat-intelligence research teams and reputable security research groups.',
  },
  {
    label: 'T3 Specialist',
    description: 'independent cybersecurity trade press with editorial standards.',
  },
  {
    label: 'T4 General',
    description: 'general-interest technology and news sources covering cybersecurity.',
  },
] as const

function PolicySection({
  title,
  children,
  id,
}: {
  title: string
  children: ReactNode
  /** Anchor target. Set where something in the product deep-links to the section. */
  id?: string
}) {
  return (
    <section
      id={id}
      className="space-y-4 scroll-mt-6 rounded-lg border border-border bg-card p-5 shadow-sm"
    >
      <h2 className="text-xl font-semibold text-foreground">{title}</h2>
      {children}
    </section>
  )
}

export function TrustPage() {
  return (
    <div className="h-screen overflow-y-auto bg-background text-foreground">
      <main className="mx-auto flex min-h-full w-full max-w-5xl flex-col px-4 py-6 sm:px-6 lg:px-8">
        <header className="flex flex-col gap-6 border-b border-border pb-8 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-4">
            <span
              className="block"
              role="img"
              aria-label="CyberFind"
              data-testid="cyberfind-trust-brand-mark"
            >
              <img
                src="/icons/cyberfind-logo-light.svg"
                alt=""
                aria-hidden
                className="h-11 w-auto dark:hidden"
              />
              <img
                src="/icons/cyberfind-logo-dark.svg"
                alt=""
                aria-hidden
                className="hidden h-11 w-auto dark:block"
              />
            </span>
            <div className="max-w-3xl space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                CyberFind trust &amp; independence
              </p>
              <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                Who pays CyberFind?
              </h1>
              <p className="text-lg leading-8 text-muted-foreground">
                CyberFind is built for security decision-makers, not for vendor
                placement.
              </p>
            </div>
          </div>
          <Link
            to="/login"
            className="inline-flex min-h-11 items-center rounded-md border border-border px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Back to IntelHub sign in
          </Link>
        </header>

        <article className="space-y-6 py-8 text-base leading-7 text-foreground">
          <section className="rounded-lg border border-primary/20 bg-primary/5 p-5">
            <p className="max-w-3xl text-lg leading-8">
              CyberFind is built for verified CISOs and security leaders. Our
              role is to help buyers understand security products, threat
              intelligence, regulatory exposure, and vendor evidence without
              turning vendor payment into ranking influence.
            </p>
          </section>

          <PolicySection title="Our independence rule">
            <div className="space-y-4 text-muted-foreground">
              <p>
                CyberFind does not sell vendor products. CyberFind does not take
                commissions for vendor introductions. CyberFind does not sell
                paid placement in recommendations, rankings, intelligence
                summaries, or community surfaces.
              </p>
              <p>
                Our commercial path is buyer-aligned: paid subscriptions for
                invited, verified CISOs and security leaders to access
                CyberFind products, plus concierge support, decision workflow
                tooling, and future metered access to clearly scoped CyberFind
                data. Any future paid tier must preserve the same rule: payment
                cannot buy a better ranking, a stronger recommendation, or a
                cleaner confidence label, and vendors cannot buy access or
                influence.
              </p>
            </div>
          </PolicySection>

          <PolicySection title="What we do not take from vendors">
            <div className="space-y-4 text-muted-foreground">
              <p>CyberFind does not accept:</p>
              <ul className="list-disc space-y-2 pl-5">
                <li>reseller fees;</li>
                <li>referral commissions;</li>
                <li>vendor remuneration for recommendations;</li>
                <li>
                  paid placement in rankings, shortlists, briefs, or
                  intelligence summaries;
                </li>
                <li>vendor-paid listings presented as editorial judgement;</li>
                <li>
                  payment to alter confidence, provenance, or verification
                  labels.
                </li>
              </ul>
              <p>
                Vendors may provide factual information about their own
                products. That information must be labelled and treated as
                source material, not as independent proof.
              </p>
            </div>
          </PolicySection>

          <PolicySection title="How CyberFind can make money without paid placement">
            <div className="space-y-4 text-muted-foreground">
              <p>
                CyberFind can charge for access that helps the buyer:
                invite-only subscriptions for verified CISOs and security
                leaders, structured concierge support, decision workflow
                tooling, and future access to CyberFind-curated data. Those
                models are acceptable only if they do not let a vendor buy
                access, influence what a CISO sees, or change how CyberFind
                grades evidence.
              </p>
              <p>
                If CyberFind later offers metered AI or API access, the same
                rule applies: access can be part of a paid CISO/security-leader
                subscription; answer quality, ranking, provenance, and
                confidence labels cannot be bought by vendors.
              </p>
            </div>
          </PolicySection>

          {/* Charter 23 T7 (KAN-150). The canonical explanation of the inline
              disclosure that appears on story pages. `EditorialCoiDisclosure`
              deep-links here via /trust#editorial-conflicts, so the id is part
              of the contract — do not rename it without updating that link. */}
          <PolicySection
            id="editorial-conflicts"
            title="When we have a commercial relationship with a vendor we write about"
          >
            <div className="space-y-4 text-muted-foreground">
              <p>
                CyberFind takes no money from vendors, but CyberFind does spend
                money with them. We pay for hosting, email delivery, error
                monitoring, log aggregation, analytics, AI model APIs, source
                control, and issue tracking, and some of those suppliers are
                companies IntelHub
                writes about. That is a commercial relationship, and you should
                not have to take our word for it that it changed nothing.
              </p>
              <p>
                So we disclose it. CyberFind maintains an internal register of
                every commercial relationship it holds with a vendor IntelHub
                might cover. When a story or research brief names a vendor in
                that register, the story carries a{' '}
                <span className="font-medium text-foreground">
                  &ldquo;commercial relationship disclosed&rdquo;
                </span>{' '}
                label and a one-line disclosure at the top, naming the vendor and
                stating what the relationship actually is. The same disclosure is
                posted with the brief in the CyberFind community.
              </p>
              <p>
                The disclosure is not optional and it is not applied by hand. Our
                publish workflow checks every brief against the register and
                refuses to publish when a required disclosure has not been
                reviewed and acknowledged by an editor. The check runs against
                the register at the moment you read the page, so a relationship
                we enter into later is disclosed on stories we already published,
                and one that ends stops being claimed.
              </p>
              <p>
                What disclosure does not mean: it does not mean the vendor paid
                for coverage, saw the story before publication, or influenced its
                confidence label. None of those things happen, for any vendor,
                disclosed or not. The disclosure exists so you can judge that
                claim rather than trust it.
              </p>
            </div>
          </PolicySection>

          <PolicySection title="How we grade intelligence confidence">
            <div className="space-y-5 text-muted-foreground">
              <p>
                CyberFind separates a claim from the evidence behind it.
                IntelHub confidence labels describe corroboration and source
                quality; they are not vendor popularity scores.
              </p>
              <div>
                <h3 className="font-medium text-foreground">Story confidence bands</h3>
                <dl className="mt-3 grid gap-3 sm:grid-cols-2">
                  {CONFIDENCE_BANDS.map((band) => (
                    <div key={band.label} className="rounded-md border border-border p-3">
                      <dt className="font-medium text-foreground">{band.label}</dt>
                      <dd className="mt-1 text-sm leading-6">{band.description}</dd>
                    </div>
                  ))}
                </dl>
              </div>
              <div>
                <h3 className="font-medium text-foreground">Source tiers</h3>
                <dl className="mt-3 grid gap-3 sm:grid-cols-2">
                  {SOURCE_TIERS.map((tier) => (
                    <div key={tier.label} className="rounded-md border border-border p-3">
                      <dt className="font-medium text-foreground">{tier.label}</dt>
                      <dd className="mt-1 text-sm leading-6">{tier.description}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </PolicySection>

          <PolicySection title="How we handle corrections">
            <div className="space-y-4 text-muted-foreground">
              <p>
                CyberFind should correct material errors and make the correction
                visible where the original claim was used. In v0, this page
                records the policy. A public corrections log is deferred until
                product/content volume makes it useful and maintainable.
              </p>
              <p>
                If a claim is uncertain, stale, single-source, or
                vendor-provided, the product should say so rather than hiding
                the uncertainty.
              </p>
            </div>
          </PolicySection>

          <PolicySection title="Machine-readable policy for future CyberFind query tools">
            <p className="text-muted-foreground">
              CyberFind may offer paid subscriptions for invited, verified CISOs
              and security leaders to access CyberFind products. CyberFind does
              not accept vendor payment for access, ranking, recommendation,
              placement, confidence labels, provenance labels, or editorial
              treatment. CyberFind query responses must preserve source
              attribution, confidence state, and stale-data warnings.
              Vendor-provided material must be labelled as vendor-provided or
              treated as an input requiring independent corroboration.
            </p>
          </PolicySection>
        </article>

        <footer className="mt-auto border-t border-border py-6 text-sm text-muted-foreground">
          <p>
            Last reviewed:{' '}
            <time dateTime="2026-07-06" className="font-medium text-foreground">
              2026-07-06
            </time>
          </p>
        </footer>
      </main>
    </div>
  )
}
